/**
 * Magma Base Component
 *
 * SVG component for player home base area using the magma skin.
 * Built from the actual Figma SVG design (blue_base.svg reference).
 * Features a purple frame with zigzag edges, dark inner pawn quadrants,
 * player-specific surface/accent gradients, and teal/sparkle decorations.
 *
 * This component is color-agnostic and accepts a colorScheme prop to render
 * bases for different players (Red, Blue, Yellow, Green).
 */

import React, { type ReactElement } from 'react';
import type { BaseProps } from '../../constants/types';
import type { PawnProps } from '../../constants/types';
import { renderPawnAtPosition } from '../basics/utils/utils';
import type { MagmaBaseColorScheme } from './constants/baseColorSchemes';

// Helper constants for rank display
const RANK_ICONS = ['🏆', '🥈', '🥉', '🏅'] as const;
const RANK_ORDINALS = ['1st', '2nd', '3rd', '4th'] as const;

function getRankIcon(rank: number): string {
  return RANK_ICONS[Math.min(rank - 1, 3)];
}

function getRankOrdinal(rank: number): string {
  if (rank >= 1 && rank <= 4) {
    return RANK_ORDINALS[rank - 1];
  }
  return `${rank.toString()}th`;
}

export interface MagmaBaseComponentProps extends BaseProps {
  colorScheme: MagmaBaseColorScheme;
  PawnComponent: React.ComponentType<PawnProps>;
  onPawn1Click?: () => void;
  onPawn2Click?: () => void;
  onPawn3Click?: () => void;
  onPawn4Click?: () => void;
  shouldAnimatePawns?: boolean;
  /** Player's finishing rank (1-4) - when set, shows rank badge instead of pawns */
  playerRank?: number;
  /** Whether the player has voluntarily left the game */
  hasLeft?: boolean;
}

const MagmaBaseComponent = React.memo(function MagmaBaseComponent({
  viewBox = '0 0 486 523',
  className = '',
  isActive = false,
  isHovered = false,
  isSelected = false,
  showEntrance = false,
  pawn1 = false,
  pawn2 = false,
  pawn3 = false,
  pawn4 = false,
  pawn1Active = false,
  pawn1Hovered = false,
  pawn1Selected = false,
  pawn1Moveable = false,
  pawn2Active = false,
  pawn2Hovered = false,
  pawn2Selected = false,
  pawn2Moveable = false,
  pawn3Active = false,
  pawn3Hovered = false,
  pawn3Selected = false,
  pawn3Moveable = false,
  pawn4Active = false,
  pawn4Hovered = false,
  pawn4Selected = false,
  pawn4Moveable = false,
  colorScheme,
  PawnComponent,
  onPawn1Click,
  onPawn2Click,
  onPawn3Click,
  onPawn4Click,
  shouldAnimatePawns = false,
  playerRank,
  hasLeft = false
}: MagmaBaseComponentProps): ReactElement {
  const {
    name,
    ariaLabel,
    surfacePrimary,
    surfaceSecondary,
    accentStart,
    accentMid,
    accentEnd,
    pawnGradientStart,
    pawnGradientMid,
    pawnGradientEnd,
    pawnSpotShadow,
    sparklePrimary,
    sparkleSecondary
  } = colorScheme;

  const svgClassName = [
    className,
    showEntrance ? 'base-entrance' : '',
    isActive ? 'base-active' : '',
    isHovered ? 'base-hovered' : '',
    isSelected ? 'base-selected' : ''
  ]
    .filter(Boolean)
    .join(' ');

  // Prefix all gradient/clip IDs to avoid conflicts when 4 bases render simultaneously
  const prefix = `${name}MagmaBase`;

  return (
    <svg
      viewBox={viewBox}
      className={svgClassName}
      xmlns="http://www.w3.org/2000/svg"
      aria-label={ariaLabel}
    >
      <defs>
        {/* Clip path from Figma */}
        <clipPath id={`${prefix}Clip0`}>
          <rect width="485.33" height="522.29" fill="white" />
        </clipPath>

        {/* paint0 — Bottom extrusion dark: #2A1C35 → black */}
        <linearGradient
          id={`${prefix}P0`}
          x1="251.78"
          y1="454.33"
          x2="251.78"
          y2="524.08"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#2A1C35" />
          <stop offset="1" />
        </linearGradient>

        {/* paint1 — Bottom extrusion light: #886F97 → black */}
        <linearGradient
          id={`${prefix}P1`}
          x1="251.8"
          y1="429.47"
          x2="251.8"
          y2="512.23"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#886F97" />
          <stop offset="1" />
        </linearGradient>

        {/* paint2 — Main surface gradient (player-specific, horizontal) */}
        <linearGradient
          id={`${prefix}P2`}
          x1="55.02"
          y1="256.19"
          x2="448.63"
          y2="256.19"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={surfacePrimary} />
          <stop offset="1" stopColor={surfaceSecondary} />
        </linearGradient>

        {/* paint3 — Top-left corner accent (player-specific) */}
        <linearGradient
          id={`${prefix}P3`}
          x1="47.69"
          y1="128.93"
          x2="117.53"
          y2="63.1998"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={accentStart} />
          <stop offset="0.52" stopColor={accentMid} />
          <stop offset="1" stopColor={accentEnd} />
        </linearGradient>

        {/* paint4 — Left edge accent (player-specific) */}
        <linearGradient
          id={`${prefix}P4`}
          x1="64.84"
          y1="219.21"
          x2="188.98"
          y2="102.36"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={accentStart} />
          <stop offset="0.52" stopColor={accentMid} />
          <stop offset="1" stopColor={accentEnd} />
        </linearGradient>

        {/* paint5 — Right mid accent (player-specific) */}
        <linearGradient
          id={`${prefix}P5`}
          x1="376.25"
          y1="364.02"
          x2="417.28"
          y2="325.4"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={accentStart} />
          <stop offset="0.52" stopColor={accentMid} />
          <stop offset="1" stopColor={accentEnd} />
        </linearGradient>

        {/* paint6 — Right bottom accent (player-specific) */}
        <linearGradient
          id={`${prefix}P6`}
          x1="390.41"
          y1="424.84"
          x2="430.94"
          y2="386.69"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={accentStart} />
          <stop offset="0.52" stopColor={accentMid} />
          <stop offset="1" stopColor={accentEnd} />
        </linearGradient>

        {/* paint7 — Bottom edge accent (player-specific) */}
        <linearGradient
          id={`${prefix}P7`}
          x1="105.91"
          y1="426.75"
          x2="318.43"
          y2="426.75"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={accentStart} />
          <stop offset="0.52" stopColor={accentMid} />
          <stop offset="1" stopColor={accentEnd} />
        </linearGradient>

        {/* paint8 — Top edge accent (player-specific) */}
        <linearGradient
          id={`${prefix}P8`}
          x1="237.6"
          y1="83.5998"
          x2="428.58"
          y2="83.5998"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={accentStart} />
          <stop offset="0.52" stopColor={accentMid} />
          <stop offset="1" stopColor={accentEnd} />
        </linearGradient>

        {/* paint9 — Small shadow accent: #2A1C35 → black */}
        <linearGradient
          id={`${prefix}P9`}
          x1="274.27"
          y1="111.94"
          x2="274.27"
          y2="126.08"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#2A1C35" />
          <stop offset="1" />
        </linearGradient>

        {/* paint10 — Pawn circle top-left (player-specific) */}
        <linearGradient
          id={`${prefix}P10`}
          x1="146.16"
          y1="191.22"
          x2="255.41"
          y2="191.22"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={pawnGradientStart} />
          <stop offset="0.52" stopColor={pawnGradientMid} />
          <stop offset="1" stopColor={pawnGradientEnd} />
        </linearGradient>

        {/* paint11 — Pawn circle top-right (player-specific) */}
        <linearGradient
          id={`${prefix}P11`}
          x1="285.69"
          y1="189.88"
          x2="396.44"
          y2="189.88"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={pawnGradientStart} />
          <stop offset="0.52" stopColor={pawnGradientMid} />
          <stop offset="1" stopColor={pawnGradientEnd} />
        </linearGradient>

        {/* paint12 — Pawn circle bottom-left (player-specific) */}
        <linearGradient
          id={`${prefix}P12`}
          x1="146.15"
          y1="323.17"
          x2="257.47"
          y2="323.17"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={pawnGradientStart} />
          <stop offset="0.52" stopColor={pawnGradientMid} />
          <stop offset="1" stopColor={pawnGradientEnd} />
        </linearGradient>

        {/* paint13 — Pawn circle bottom-right (player-specific) */}
        <linearGradient
          id={`${prefix}P13`}
          x1="285.69"
          y1="323.84"
          x2="400.88"
          y2="323.84"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={pawnGradientStart} />
          <stop offset="0.52" stopColor={pawnGradientMid} />
          <stop offset="1" stopColor={pawnGradientEnd} />
        </linearGradient>

        {/* paint14 — Left arm shadow: #2A1C35 → black */}
        <linearGradient
          id={`${prefix}P14`}
          x1="411"
          y1="258.46"
          x2="411"
          y2="293.42"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#2A1C35" />
          <stop offset="1" />
        </linearGradient>

        {/* paint15 — Right arm shadow: #2A1C35 → black */}
        <linearGradient
          id={`${prefix}P15`}
          x1="98.1901"
          y1="313.73"
          x2="98.1901"
          y2="327.07"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#2A1C35" />
          <stop offset="1" />
        </linearGradient>

        {/* paint16 — Mid shadow: #2A1C35 → black */}
        <linearGradient
          id={`${prefix}P16`}
          x1="398.3"
          y1="259.28"
          x2="398.3"
          y2="281.99"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#2A1C35" />
          <stop offset="1" />
        </linearGradient>

        {/* paint17 — Left torch accent (player-specific) */}
        <linearGradient
          id={`${prefix}P17`}
          x1="45.0199"
          y1="191.47"
          x2="71.4799"
          y2="191.47"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={accentStart} />
          <stop offset="0.52" stopColor={accentMid} />
          <stop offset="1" stopColor={accentEnd} />
        </linearGradient>

        {/* paint18 — Top-right torch: #824739 → #CAACB8 (fixed) */}
        <linearGradient
          id={`${prefix}P18`}
          x1="397.34"
          y1="70.09"
          x2="412.57"
          y2="70.09"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#824739" />
          <stop offset="1" stopColor="#CAACB8" />
        </linearGradient>

        {/* paint19 — Top-right torch large: #824739 → #CAACB8 (fixed) */}
        <linearGradient
          id={`${prefix}P19`}
          x1="410.06"
          y1="63.5401"
          x2="450.26"
          y2="63.5401"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#824739" />
          <stop offset="1" stopColor="#CAACB8" />
        </linearGradient>

        {/* paint20 — Top-left torch: #824739 → #CAACB8 (fixed) */}
        <linearGradient
          id={`${prefix}P20`}
          x1="70.6557"
          y1="133.266"
          x2="38.4199"
          y2="141.657"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#824739" />
          <stop offset="1" stopColor="#CAACB8" />
        </linearGradient>

        {/* paint21 — Bottom-right torch: #824739 → #CAACB8 (fixed) */}
        <linearGradient
          id={`${prefix}P21`}
          x1="425.626"
          y1="323.743"
          x2="457.852"
          y2="332.131"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#824739" />
          <stop offset="1" stopColor="#CAACB8" />
        </linearGradient>

        {/* paint22 — Left torch small: #824739 → #CAACB8 (fixed) */}
        <linearGradient
          id={`${prefix}P22`}
          x1="63.6258"
          y1="185.864"
          x2="47.623"
          y2="193.334"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#824739" />
          <stop offset="1" stopColor="#CAACB8" />
        </linearGradient>

        {/* paint23 — Bottom-left torch accent (player-specific) */}
        <linearGradient
          id={`${prefix}P23`}
          x1="46.4999"
          y1="395.11"
          x2="72.9699"
          y2="395.11"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={accentStart} />
          <stop offset="0.52" stopColor={accentMid} />
          <stop offset="1" stopColor={accentEnd} />
        </linearGradient>

        {/* paint24 — Bottom-left torch: #824739 → #CAACB8 (fixed) */}
        <linearGradient
          id={`${prefix}P24`}
          x1="65.1634"
          y1="388.599"
          x2="49.1606"
          y2="396.068"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#824739" />
          <stop offset="1" stopColor="#CAACB8" />
        </linearGradient>

        {/* Embedded texture pattern */}
        <pattern
          id={`${prefix}Pattern`}
          patternContentUnits="objectBoundingBox"
          width="1"
          height="1"
        >
          <use xlinkHref={`#${prefix}Image`} transform="scale(0.00213675 0.00221239)" />
        </pattern>

        {/* Base64 texture image from Figma */}
        <image
          id={`${prefix}Image`}
          width="468"
          height="452"
          preserveAspectRatio="none"
          xlinkHref="data:image/png;base64,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"
        />
      </defs>

      {/*
        Translate magma content by (-10, -26) to align pawn spots with the basics skin.
        The magma relief/flame decorations push the inner content ~10px right and ~26px down
        compared to basics. This offset ensures pawn circles align with the shared
        PAWN_POSITIONS used by renderPawnAtPosition().
      */}
      <g transform="translate(-10, -26)">
        <g clipPath={`url(#${prefix}Clip0)`}>
          {/* Drop shadow — outer bottom dark (opacity 0.12) */}
          <g opacity="0.12">
            <path
              d="M54.99 446.94V480.59C54.99 490.44 60.26 499.63 68.94 504.29C69.01 504.33 69.08 504.36 69.15 504.4C79.21 509.71 88.7 508.49 100.17 507.96C114.15 507.31 128.13 509.37 142.12 509.36C170 509.36 197.89 509.4 225.77 509.36C245.81 509.33 265.56 509.35 279.68 509.36C301.3 509.38 312.12 509.39 313.24 509.41C360.75 510.15 364.53 513.36 381.38 509.41C393.03 506.68 397.2 503.74 408.08 503.89C418.39 504.03 424.99 506.81 433.51 503.38C440.47 500.57 445.46 493.97 445.46 493.97C445.47 493.95 445.49 493.94 445.5 493.92C447.52 491.57 448.58 488.55 448.55 485.45C448.41 470.83 448.27 456.21 448.13 441.59C385.3 451.02 312.34 457.78 231.15 457.74C166.59 457.7 107.52 453.37 54.98 446.93L54.99 446.94Z"
              fill="black"
            />
            <path
              d="M54.99 423.2V467.61C57.69 471.61 62.16 476.97 69.14 480.66C79.2 485.97 88.69 484.75 100.16 484.22C114.14 483.57 128.12 485.63 142.11 485.62C169.99 485.62 197.88 485.66 225.76 485.62C245.8 485.59 265.55 485.61 279.67 485.62C301.29 485.64 312.11 485.65 313.23 485.67C360.74 486.41 364.52 489.62 381.37 485.67C393.02 482.94 397.19 480 408.07 480.15C418.38 480.29 424.98 483.07 433.5 479.64C440.46 476.83 445.45 470.23 445.45 470.23C446.85 468.61 447.89 467.14 448.59 466.08C448.43 450 448.28 433.93 448.12 417.85C385.29 427.28 312.33 434.04 231.14 434C166.58 433.96 107.51 429.63 54.97 423.19L54.99 423.2Z"
              fill="black"
            />
            <path
              d="M411.71 91.2998H91.88C71.5062 91.2998 54.99 107.816 54.99 128.19V430.73C54.99 451.104 71.5062 467.62 91.88 467.62H411.71C432.084 467.62 448.6 451.104 448.6 430.73V128.19C448.6 107.816 432.084 91.2998 411.71 91.2998Z"
              fill="black"
            />
            <path
              d="M377.38 124.12H126.21C105.836 124.12 89.3201 140.636 89.3201 161.01V397.91C89.3201 418.284 105.836 434.8 126.21 434.8H377.38C397.754 434.8 414.27 418.284 414.27 397.91V161.01C414.27 140.636 397.754 124.12 377.38 124.12Z"
              fill="black"
            />
            <path
              d="M378.57 131.97H125.01C109.828 131.97 97.52 144.277 97.52 159.46V399.46C97.52 414.642 109.828 426.95 125.01 426.95H378.57C393.752 426.95 406.06 414.642 406.06 399.46V159.46C406.06 144.277 393.752 131.97 378.57 131.97Z"
              fill="black"
            />
          </g>

          {/* Bottom extrusion shadow dark */}
          <path
            d="M54.99 423.67V457.32C54.99 467.17 60.26 476.36 68.94 481.02C69.01 481.06 69.08 481.09 69.15 481.13C79.21 486.44 88.7 485.22 100.17 484.69C114.15 484.04 128.13 486.1 142.12 486.09C170 486.09 197.89 486.13 225.77 486.09C245.81 486.06 265.56 486.08 279.68 486.09C301.3 486.11 312.12 486.12 313.24 486.14C360.75 486.88 364.53 490.09 381.38 486.14C393.03 483.41 397.2 480.47 408.08 480.62C418.39 480.76 424.99 483.54 433.51 480.11C440.47 477.3 445.46 470.7 445.46 470.7C445.47 470.68 445.49 470.67 445.5 470.65C447.52 468.3 448.58 465.28 448.55 462.18C448.41 447.56 448.27 432.94 448.13 418.32C385.3 427.75 312.34 434.51 231.15 434.47C166.59 434.43 107.52 430.1 54.98 423.66L54.99 423.67Z"
            fill={`url(#${prefix}P0)`}
          />

          {/* Bottom extrusion shadow light */}
          <path
            d="M54.99 399.94V444.35C57.69 448.35 62.16 453.71 69.14 457.4C79.2 462.71 88.69 461.49 100.16 460.96C114.14 460.31 128.12 462.37 142.11 462.36C169.99 462.36 197.88 462.4 225.76 462.36C245.8 462.33 265.55 462.35 279.67 462.36C301.29 462.38 312.11 462.39 313.23 462.41C360.74 463.15 364.52 466.36 381.37 462.41C393.02 459.68 397.19 456.74 408.07 456.89C418.38 457.03 424.98 459.81 433.5 456.38C440.46 453.57 445.45 446.97 445.45 446.97C446.85 445.35 447.89 443.88 448.59 442.82C448.43 426.74 448.28 410.67 448.12 394.59C385.29 404.02 312.33 410.78 231.14 410.74C166.58 410.7 107.51 406.37 54.97 399.93L54.99 399.94Z"
            fill={`url(#${prefix}P1)`}
          />

          {/* Main surface fill (player-specific) */}
          <path
            d="M448.63 104.92V407.46C448.63 427.83 432.11 444.35 411.74 444.35H91.91C85.85 444.35 80.14 442.89 75.09 440.3C63.17 434.19 55.02 421.77 55.02 407.46V104.92C55.02 86.2298 68.92 70.7798 86.96 68.3598C88.58 68.1398 90.23 68.0298 91.91 68.0298H411.74C413.34 68.0298 414.9 68.1298 416.45 68.3298H416.46C422.44 69.0898 427.98 71.2898 432.71 74.5698C437.49 77.8798 441.45 82.2798 444.22 87.4198C447.03 92.6298 448.63 98.5898 448.63 104.92Z"
            fill={`url(#${prefix}P2)`}
          />

          {/* Top-left corner accent (player-specific) */}
          <path
            d="M132.14 68.0298C129.26 70.0398 126.03 72.3198 122.45 74.9298C111.03 83.2598 105.32 87.4198 101.56 91.1298C84.07 108.37 88.27 117.75 73.56 127.38C66.06 132.29 62.6 131.39 55.02 135.7V116.5C56.17 116.06 57.36 115.55 58.58 114.93C75.74 106.29 82.99 86.9198 83.79 84.7098C86.51 77.1298 86.22 72.4098 86.96 68.3598C88.58 68.1398 90.23 68.0298 91.91 68.0298H132.14Z"
            fill={`url(#${prefix}P3)`}
          />

          {/* Left edge accent (player-specific) */}
          <path
            d="M167.42 68.0298C167.06 72.1698 165.57 77.2598 162.9 81.5998C158.6 88.5898 154.17 88.8398 146.9 94.9298C135.59 104.42 129.41 118 127.34 128.27C123.94 145.17 132.11 150.93 126.9 158.04C119.59 168.01 102.95 157.48 84.23 167.82C68.4 176.57 62.66 193.85 61.56 197.16C59.83 202.39 58.52 209.1 62.9 246.04C66.21 273.98 68.25 281.07 62.9 287.82C60.87 290.38 58.09 292.5 55.02 294.02V164.51C56.15 163.72 57.33 162.9 58.58 162.04C100.3 133.29 102.38 136.61 109.56 126.93C119.59 113.43 116.75 105.34 130.9 90.0398C131.72 89.1598 136.78 84.4198 146.9 74.9298C149.83 72.1798 152.28 69.8998 154.32 68.0298H167.42Z"
            fill={`url(#${prefix}P4)`}
          />

          {/* Right mid accent (player-specific) */}
          <path
            d="M448.63 291.9V314.61C448.09 315.01 447.53 315.41 446.97 315.82C435.55 324.15 429.84 328.31 426.08 332.01C408.58 349.26 412.78 358.64 398.08 368.27C389.09 374.15 385.92 371.7 374.52 379.82C364.22 387.16 361.67 392.83 357.63 391.82C351.17 390.21 346.2 372.8 352.74 364.27C358.63 356.59 368.39 363.22 383.09 355.82C400.25 347.18 407.5 327.81 408.3 325.6C412.69 313.38 409.24 308.6 414.97 302.04C416.48 300.31 420.73 296.06 441.21 292.97C443.11 292.69 445.7 292.3 448.63 291.9Z"
            fill={`url(#${prefix}P5)`}
          />

          {/* Right bottom accent (player-specific) */}
          <path
            d="M448.63 339.49V401.55C440.37 406.81 425.38 399.52 408.74 408.71C392.91 417.46 387.17 434.74 386.08 438.04C385.53 439.7 385.02 441.5 384.73 444.35H363.37C361.25 433.87 359.83 431.08 361.19 425.6C363.31 417.05 369.67 412.18 383.09 402.93C424.82 374.18 426.9 377.5 434.08 367.82C442.06 357.07 441.88 349.75 448.63 339.49Z"
            fill={`url(#${prefix}P6)`}
          />

          {/* Bottom edge accent (player-specific) */}
          <path
            d="M335.83 444.35H226.02C223.96 442.19 221.34 440.06 217.56 438.04C202.77 430.15 186.86 432.91 183.19 433.64C172.47 435.78 166.92 440.46 162.01 444.35H132.03C124.99 438.79 119.66 431.75 110.9 433.7C106.37 434.7 99.4901 438.44 94.6201 444.35H91.9101C85.8501 444.35 80.1401 442.89 75.0901 440.3C79.4701 433.94 87.0501 428.34 88.3801 427.38C91.1201 425.39 108.11 412.79 120.97 419.28C125.21 421.42 124.98 423.86 130.45 427.38C139.84 433.41 152.35 433.85 161.86 430.34C168.4 427.93 167.07 426.03 178.45 419.28C186.96 414.23 192.73 410.81 200.97 409.6C212.79 407.86 222.42 411.72 225.86 413.16C236.81 417.7 236.98 422.33 246.01 426.19C253.96 429.59 258.54 428.01 277.42 426.79C301.68 425.21 313.97 424.51 321.86 429.16C327.83 432.67 332.97 438.55 335.83 444.35Z"
            fill={`url(#${prefix}P7)`}
          />

          {/* Top edge accent (player-specific) */}
          <path
            d="M444.22 87.4198C437.72 90.4298 429.44 92.5998 422.4 89.0498C418.16 86.9098 418.39 84.4698 412.91 80.9498C403.53 74.9098 391.02 74.4798 381.51 77.9898C374.97 80.3998 376.3 82.2998 364.91 89.0498C356.41 94.0898 350.64 97.5098 342.4 98.7298C330.58 100.47 320.95 96.5998 317.51 95.1698C306.56 90.6198 306.39 85.9898 297.36 82.1298C289.41 78.7398 284.83 80.3098 265.95 81.5398C241.69 83.1198 229.4 83.8198 221.51 79.1698C216.93 76.4798 212.85 72.3998 209.9 68.0298H322.1C323.2 68.7898 324.42 69.5398 325.8 70.2798C340.6 78.1798 356.51 75.4198 360.17 74.6898C367.3 73.2698 372.14 70.7198 376.03 68.0298H411.74C413.34 68.0298 414.9 68.1298 416.45 68.3298H416.46C421.31 72.5398 425.94 76.0898 432.47 74.6298C432.55 74.6098 432.63 74.5898 432.71 74.5698C437.49 77.8798 441.45 82.2798 444.22 87.4198Z"
            fill={`url(#${prefix}P8)`}
          />

          {/* Sparkle top-left — large (player-specific) */}
          <path
            d="M91.7901 72.4098C91.0201 72.2998 89.3901 76.0598 86.1601 83.5998C78.6701 101.06 78.0401 102.55 76.2201 105.01C69.8501 113.61 62.1301 116.25 62.7601 117.45C63.3701 118.61 70.8501 116.79 77.2801 112.71C79.5501 111.27 87.8701 105.81 91.8001 94.9298C95.6001 84.3898 93.8501 72.7098 91.8001 72.4098H91.7901Z"
            fill={sparklePrimary}
          />

          {/* Sparkle left edge — large (player-specific) */}
          <path
            d="M62.7199 174.04C64.0299 175.28 68.9499 170.06 78.3799 165.6C89.3499 160.4 100.79 158.89 103.94 158.49C113.67 157.27 118.25 158.36 120.83 154.93C122.5 152.71 120.77 151.03 120.83 142.93C120.86 138.12 120.89 134.31 122.28 129.89C124.47 122.91 128.03 120.84 127.05 116.26C126.65 114.4 125.47 111.95 123.94 111.82C122.31 111.68 121.04 114.2 117.27 121.01C113.83 127.22 112.11 130.32 111.22 131.53C107.89 136.02 104.23 138.49 99.9399 141.31C93.1399 145.79 92.0899 144.86 84.8299 149.01C73.4999 155.49 68.5199 162.06 66.6099 164.86C66.0699 165.65 61.3099 172.7 62.7199 174.05V174.04Z"
            fill={sparklePrimary}
          />

          {/* Sparkle top edge — large (player-specific) */}
          <path
            d="M248.83 71.8198C261.65 71.5698 270.53 71.7698 276.68 71.8198C296.03 71.9898 295.14 70.8398 303.64 71.8198C315.56 73.1998 327.06 76.5598 327.06 76.5598C338.11 79.7998 341.33 82.2398 347.79 80.9998C349.16 80.7398 352.85 79.7398 360.23 77.7398C366.24 76.1098 368.64 75.3898 369.12 76.1798C369.85 77.3798 365.69 81.3298 362.9 83.5898C359.27 86.5298 349.51 94.4298 337.12 93.7398C329.33 93.3098 329.07 89.9498 310.75 82.5198C302.2 79.0498 297.92 77.3198 293.56 76.5498C281.7 74.4698 270.16 76.9098 257.84 79.5098C251.67 80.8098 245.26 82.5198 238.53 79.5098C235.79 78.2798 232.11 75.8798 232.51 74.1798C232.92 72.3898 237.68 72.0298 248.82 71.8098L248.83 71.8198Z"
            fill={sparklePrimary}
          />

          {/* Sparkle bottom edge — large (player-specific) */}
          <path
            d="M115.79 422.93C122.6 424.53 123.11 427.95 130.31 431.52C141.53 437.09 153.07 435.12 154.9 434.78C157.45 434.31 165.09 430.52 180.38 422.93C184.64 420.81 187.75 419.24 192.53 417.89C194.58 417.31 200.15 415.88 207.64 415.82C212.3 415.78 220.5 415.8 229.27 419.97C233.34 421.9 239.02 424.6 239.05 428.56C239.07 431.55 235.86 434.2 233.12 435.38C227.79 437.67 222.53 435.13 217.12 433.31C216.53 433.11 202.21 428.4 190.16 429.75C176.92 431.24 176.66 438.89 159.94 441.9C154.45 442.89 148.3 443.99 140.98 441.9C132.3 439.42 131.77 435.25 121.42 432.12C116.66 430.68 109.68 428.57 103.05 431.53C97.6101 433.96 95.3601 438.48 94.1601 437.75C92.8701 436.97 93.8401 430.74 97.7201 426.76C103.97 420.33 114.87 422.72 115.79 422.94V422.93Z"
            fill={sparklePrimary}
          />

          {/* Sparkle right bottom — large (player-specific) */}
          <path
            d="M381.86 438.04C388.13 434.75 385.66 426.36 391.64 416.71C396.22 409.32 402.92 405.75 404.64 404.86C411.17 401.48 418.18 400.8 431.35 399.53C439.75 398.72 442.85 398.92 443.79 396.86C445.31 393.54 440.22 386.42 433.72 383.82C425.38 380.49 416.58 385.36 407.05 390.63C402.23 393.3 393.08 398.49 385.42 409.15C383.77 411.45 381.26 417.05 376.23 428.26C374.08 433.05 372.71 436.36 374.16 438.04C375.54 439.64 379.22 439.43 381.86 438.04Z"
            fill={sparklePrimary}
          />

          {/* Sparkle right mid — large (player-specific) */}
          <path
            d="M365.86 363.38C371.49 358.9 376.3 360.65 383.93 358.34C393.53 355.44 399.41 348.58 401.71 345.9C408.87 337.55 411.47 327.42 412.97 321.6C415.18 313 414.43 309.77 418.01 306.19C421.69 302.51 428.15 300.27 434.01 302.34C434.9 302.65 440.76 304.72 441.42 308.86C442.2 313.77 434.96 317.19 427.2 324.27C415.1 335.31 418.86 339.63 405.87 353.9C392.95 368.09 382.21 371.54 379.8 372.27C372.7 374.41 362.27 375.63 360.66 372.27C359.53 369.91 362.96 365.69 365.87 363.38H365.86Z"
            fill={sparklePrimary}
          />

          {/* Sparkle top-left — small (player-specific) */}
          <path
            d="M102.16 154.27C103.54 156.48 109.89 155.91 113.94 152.71C114.75 152.07 117.79 149.67 117.27 147.38C116.66 144.66 111.21 142.82 107.27 144.49C103.31 146.17 102.34 150.77 102.16 151.6C101.9 152.84 101.74 153.59 102.16 154.27Z"
            fill={sparkleSecondary}
          />

          {/* Sparkle top-left corner — small (player-specific) */}
          <path
            d="M90.16 80.2697C88.72 80.0197 85.49 86.2697 87.05 87.9697C87.72 88.7097 89.26 88.5597 90.16 87.9697C92.02 86.7397 91.36 83.4397 91.2 82.6097C90.99 81.5697 90.75 80.3697 90.16 80.2697Z"
            fill={sparkleSecondary}
          />

          {/* Sparkle top edge — small (player-specific) */}
          <path
            d="M354.05 81.1599C352.25 79.2799 348.04 83.6199 341.49 83.6099C335.06 83.5999 333.88 79.3999 326.16 78.9399C321.57 78.6699 316.53 79.8199 316.38 81.2799C316.28 82.2199 318.24 82.9399 327.05 86.4999C337.33 90.6499 338.05 91.0399 340.15 91.1399C345.49 91.3999 352.52 89.4899 354.18 85.0199C354.29 84.7099 355.15 82.3199 354.04 81.1699L354.05 81.1599Z"
            fill={sparkleSecondary}
          />

          {/* Sparkle right mid — small (player-specific) */}
          <path
            d="M433.42 307.67C432.13 305.55 428.51 305.22 426.01 305.89C421.99 306.97 420.01 310.85 417.71 315.37C415.83 319.06 412.77 326.42 414.45 327.52C415.15 327.98 416.21 327.06 421.26 323.37C429.66 317.24 432.24 315.77 433.41 312.11C433.75 311.04 434.32 309.17 433.41 307.67H433.42Z"
            fill={sparkleSecondary}
          />

          {/* Sparkle right bottom — small (player-specific) */}
          <path
            d="M430.16 385.6C427.98 383.95 424.45 386.96 413.97 391.92C402.67 397.27 396.16 398.79 396.38 401.3C396.49 402.48 398.03 403.38 399.14 403.67C402.06 404.44 404.76 401.72 407.04 400.11C416.23 393.68 425.9 400.37 430.15 394.73C432.3 391.87 432.13 387.09 430.15 385.59L430.16 385.6Z"
            fill={sparkleSecondary}
          />

          {/* Sparkle bottom edge — small (player-specific) */}
          <path
            d="M232.23 423.82C227.6 417.61 216.89 418.32 207.34 419.08C194.5 420.1 177.81 423.73 178.01 426.75C178.11 428.32 182.75 429.71 186.6 429.45C190 429.22 191.73 427.76 194.01 426.75C199.25 424.43 202.49 426.65 219.49 429.47C229.76 431.17 232.76 431.06 233.71 429.45C234.88 427.46 232.41 424.06 232.23 423.82Z"
            fill={sparkleSecondary}
          />

          {/* Inner dark ground fill */}
          <path
            d="M98.8999 424.41C115.73 440.15 140.93 422.31 185.71 424.41C223.64 426.19 297.12 419.97 314.6 424.41C329.78 428.27 381.22 436.94 404.38 424.41C407.06 422.96 411.47 418.86 414.27 410.78C414.27 410.78 417.33 395.75 417.33 381.61C417.33 351.1 302.77 399.77 178.02 389.23C137.58 385.82 86.1099 361.33 86.1099 378.92C86.1099 388.83 90.5399 416.58 98.8999 424.4V424.41Z"
            fill="#2A1C35"
          />

          {/* Small shadow accent path */}
          <path
            d="M278.48 111.94V114.01L270.43 126.08L270.06 125.19L278.48 111.94Z"
            fill={`url(#${prefix}P9)`}
          />

          {/* Purple frame — main body */}
          <path
            d="M402.77 247.73L393.58 267.92L416.54 294.4L416.63 294.51L417.48 295.49V377C417.48 378.47 417.45 379.89 417.4 381.27C416.77 397.78 412.19 408.16 399.3 412.03C399.18 412.07 399.06 412.11 398.94 412.14C395.22 413.21 390.82 413.75 385.64 413.75H118.82C98.0599 413.75 89.2799 405.86 86.8799 389.41C86.4599 386.54 86.2299 383.4 86.1799 380C86.1699 379.37 86.1599 378.74 86.1599 378.1L87.8699 367.44L93.9899 329.21L110.32 325.71L86.1599 313.9V130.51C86.1599 106.34 94.0299 96.2598 119.09 96.2598H268.2L278.53 112.1L269.55 126.58L286.32 110.93L307.33 96.2598H379.91C386.71 96.2598 392.59 96.7398 397.55 97.9398C403.84 99.4498 408.65 102.1 411.96 106.35C415.64 111.06 417.48 117.74 417.48 127V241.32L404.69 272.84L402.77 247.73Z"
            fill="#886F97"
          />

          {/* Frame facet — top-left corner group */}
          <path
            d="M131 109.64L115 136.31H86.1599V130.51C86.1599 106.34 94.0299 96.2598 119.09 96.2598H120.55L120.89 96.6898L131 109.64Z"
            fill="#886F97"
          />
          <path d="M128.76 171.27H86.1599V136.31H115L128.76 171.27Z" fill="#7E6489" />
          <path d="M131 109.64L115 136.31L128.77 171.27L162.55 128.6L131 109.64Z" fill="#695875" />
          <path
            d="M119.09 96.2598L131 109.64L162.55 128.6L186.11 112.43L119.09 96.2598Z"
            fill="#7E6489"
          />
          <path
            d="M219.05 96.2598L186.11 112.43L120.89 96.6898L119.09 96.2598H219.05Z"
            fill="#695875"
          />
          <path d="M86.1599 171.27V208.46L109.96 223.27L128.77 171.27H86.1599Z" fill="#695875" />
          <path
            d="M145.5 150.14L128.77 171.27L119.09 198.02L169.81 169.05L145.5 150.14Z"
            fill="#886F97"
          />
          <path
            d="M109.96 223.27L119.09 198.02L140.2 185.97V206.09L109.96 223.27Z"
            fill="#7E6489"
          />
          <path d="M140.2 185.97L169.81 169.05L182.26 175.27L140.2 206.09V185.97Z" fill="#695875" />
          <path
            d="M145.5 150.14L162.55 128.6L200.33 140.46L169.81 169.05L145.5 150.14Z"
            fill="#7E6489"
          />
          <path
            d="M219.05 96.2598L200.33 140.46L162.55 128.6L186.11 112.43L219.05 96.2598Z"
            fill="#886F97"
          />

          {/* Frame facet — bottom-right corner group */}
          <path
            d="M417.4 381.27C416.77 397.78 412.19 408.16 399.3 412.03C399.18 412.07 399.06 412.11 398.94 412.14L395.66 407.94L411.66 381.27H417.4Z"
            fill="#886F97"
          />
          <path
            d="M417.48 346.31V377C417.48 378.47 417.45 379.89 417.4 381.27H411.66L397.9 346.31H417.48Z"
            fill="#7E6489"
          />
          <path
            d="M395.67 407.94L411.67 381.27L397.9 346.31L364.11 388.98L395.67 407.94Z"
            fill="#695875"
          />
          <path
            d="M399.3 412.03C399.18 412.07 399.06 412.11 398.94 412.14C395.22 413.21 390.82 413.75 385.64 413.75H376.19L340.55 405.15L364.11 388.98L395.66 407.94L399.3 412.03Z"
            fill="#7E6489"
          />
          <path d="M340.55 405.15L376.19 413.75H323.03L340.55 405.15Z" fill="#695875" />
          <path
            d="M417.48 295.49V346.31H397.9L407.57 319.56L416.63 294.51L417.48 295.49Z"
            fill="#695875"
          />
          <path
            d="M381.16 367.44L397.9 346.31L407.57 319.56L356.85 348.53L381.16 367.44Z"
            fill="#886F97"
          />
          <path
            d="M416.7 294.31L416.63 294.51L407.57 319.56L386.46 331.62V311.49L416.54 294.4L416.7 294.31Z"
            fill="#7E6489"
          />
          <path
            d="M386.47 331.61L356.85 348.53L344.41 342.31L386.47 311.49V331.61Z"
            fill="#695875"
          />
          <path
            d="M381.16 367.44L364.11 388.98L326.33 377.12L356.85 348.53L381.16 367.44Z"
            fill="#7E6489"
          />
          <path
            d="M364.11 388.98L340.55 405.15L323.03 413.75H310.82L326.33 377.12L364.11 388.98Z"
            fill="#886F97"
          />

          {/* Frame facet — left side group */}
          <path
            d="M86.1599 208.46L109.96 223.27L117.33 255L86.1599 242.46V208.46Z"
            fill="#7E6489"
          />
          <path d="M133.36 209.98V246.9L117.33 255L109.96 223.27L133.36 209.98Z" fill="#695875" />
          <path d="M117.33 255L121.66 287.2L86.1599 284.53V242.46L117.33 255Z" fill="#695875" />
          <path
            d="M133.36 246.9L154.67 264.83L121.66 287.2L117.33 255L133.36 246.9Z"
            fill="#886F97"
          />
          <path
            d="M86.1599 284.53V313.9L110.32 325.71L121.66 287.2L86.1599 284.53Z"
            fill="#7E6489"
          />
          <path
            d="M154.67 264.83L140.2 298.16L110.32 325.71L121.66 287.2L154.67 264.83Z"
            fill="#695875"
          />
          <path
            d="M110.32 325.71L101.75 379.05L87.8699 367.44L93.9899 329.21L110.32 325.71Z"
            fill="#695875"
          />

          {/* Frame facet — right side group */}
          <path
            d="M417.48 241.53L393.68 226.71L386.31 194.98L417.48 207.53V241.53Z"
            fill="#7E6489"
          />
          <path
            d="M370.29 240.01V203.09L386.31 194.98L393.68 226.71L370.29 240.01Z"
            fill="#695875"
          />
          <path
            d="M386.31 194.98L381.98 162.79L417.48 165.46V207.53L386.31 194.98Z"
            fill="#695875"
          />
          <path
            d="M417.48 165.46V136.09L393.32 124.28L381.98 162.79L417.48 165.46Z"
            fill="#7E6489"
          />
          <path
            d="M348.98 185.16L363.44 151.83L393.32 124.28L381.98 162.79L348.98 185.16Z"
            fill="#695875"
          />
          <path
            d="M411.96 106.35L409.65 120.78L393.32 124.28L397.55 97.9397C403.84 99.4497 408.65 102.1 411.96 106.35Z"
            fill="#695875"
          />

          {/* Frame facet — top right section */}
          <path
            d="M219.05 95.9097L233.86 119.71L265.6 127.08L253.05 95.9097H219.05Z"
            fill="#7E6489"
          />
          <path d="M220.57 143.1H257.49L265.6 127.08L233.86 119.71L220.57 143.1Z" fill="#695875" />
          <path
            d="M265.6 127.08L297.79 131.41L295.12 95.9097H253.05L265.6 127.08Z"
            fill="#695875"
          />
          <path
            d="M295.12 95.9097H324.49L336.3 120.07L297.79 131.41L295.12 95.9097Z"
            fill="#7E6489"
          />
          <path
            d="M275.42 164.41L308.75 149.95L336.3 120.07L297.79 131.41L275.42 164.41Z"
            fill="#695875"
          />
          <path
            d="M336.3 120.07L366.04 131.41L354.18 95.9097L336.3 102.09V120.07Z"
            fill="#695875"
          />

          {/* Frame facet — bottom left section */}
          <path
            d="M322.51 413.77L307.7 389.97L275.97 382.59L288.51 413.77H322.51Z"
            fill="#7E6489"
          />
          <path
            d="M320.99 366.57H284.07L275.97 382.59L307.7 389.97L320.99 366.57Z"
            fill="#695875"
          />
          <path
            d="M275.97 382.59L243.77 378.27L246.44 413.76L288.51 413.77L275.97 382.59Z"
            fill="#695875"
          />
          <path
            d="M246.44 413.76H217.07L205.26 389.6L243.77 378.27L246.44 413.76Z"
            fill="#7E6489"
          />
          <path
            d="M266.14 345.26L232.81 359.73L205.26 389.6L243.77 378.27L266.14 345.26Z"
            fill="#695875"
          />
          <path
            d="M205.26 389.6L151.92 398.18L163.53 412.06L201.76 405.93L205.26 389.6Z"
            fill="#695875"
          />
          <path
            d="M121.66 413.75H118.82C98.0599 413.75 89.2799 405.86 86.8799 389.41C86.4599 386.54 86.2299 383.4 86.1799 380L87.8699 367.44L106.22 383.05L121.66 413.75Z"
            fill="#886F97"
          />
          <path
            d="M354.18 95.9097L380.55 96.2597L371.15 126.09L361.96 119.21L354.18 95.9097Z"
            fill="#7E6489"
          />

          {/* Texture overlay */}
          <rect x="18.0601" y="30.6299" width="467" height="451" fill={`url(#${prefix}Pattern)`} />

          {/* Inner dark quadrant — top-right shadow */}
          <path
            d="M391.34 260.61V357.13C391.34 377.5 374.82 394.02 354.45 394.02H251.8V260.61H391.34Z"
            fill="#130F16"
          />
          {/* Inner dark quadrant — bottom-left shadow */}
          <path
            d="M112.26 260.61H251.79V394.01H149.15C128.78 394.01 112.26 377.5 112.26 357.13V260.61Z"
            fill="#130F16"
          />
          {/* Inner quadrant fill — top-right: #473C4C */}
          <path
            d="M391.36 255.83L251.83 255.83L251.83 122.43L354.48 122.43C374.84 122.43 391.37 138.96 391.37 159.32V255.84L391.36 255.83Z"
            fill="#473C4C"
          />
          {/* Inner quadrant fill — bottom-left: #473C4C */}
          <path
            d="M112.25 255.84H251.78V389.24H149.13C128.77 389.24 112.24 372.71 112.24 352.35V255.83L112.25 255.84Z"
            fill="#473C4C"
          />
          {/* Small opacity decorations */}
          <path
            opacity="0.38"
            d="M112.29 159.32V160.32L123.77 175.18L124.14 174.69L112.29 159.32Z"
            fill="#1A1A1A"
          />
          <path
            opacity="0.38"
            d="M112.26 221.47V222.48L115.64 226.05L115.85 225.73L112.26 221.47Z"
            fill="#1A1A1A"
          />
          <path
            opacity="0.38"
            d="M115.85 225.73L115.64 226.05L124.02 223.17L124.41 222.2L115.85 225.73Z"
            fill="#1A1A1A"
          />
          <path
            opacity="0.38"
            d="M112.24 182.66V184.91L115.71 169.92H115.1L112.24 182.66Z"
            fill="#1A1A1A"
          />

          {/* Inner quadrant fill — bottom-right: #1E1921 */}
          <path
            d="M251.82 389.24V255.84H391.35V352.36C391.35 372.72 374.82 389.25 354.46 389.25H251.82V389.24Z"
            fill="#1E1921"
          />
          {/* Inner quadrant fill — top-left: #1E1921 */}
          <path
            d="M251.78 122.43V255.83H112.25V159.31C112.25 138.95 128.78 122.42 149.14 122.42H251.78V122.43Z"
            fill="#1E1921"
          />

          {/* Pawn spots — only visible when game is active (no rank, not left) */}
          {playerRank === undefined && !hasLeft && (
            <>
              {/* Pawn spot 1 top-left — shadow */}
              <path
                d="M183.17 223.04C203.61 223.04 220.18 207.589 220.18 188.53C220.18 169.471 203.61 154.02 183.17 154.02C162.73 154.02 146.16 169.471 146.16 188.53C146.16 207.589 162.73 223.04 183.17 223.04Z"
                fill={pawnSpotShadow}
              />
              {/* Pawn spot 1 top-left — fill (player-specific) */}
              <path
                d="M183.17 225.73C203.61 225.73 220.18 210.279 220.18 191.22C220.18 172.161 203.61 156.71 183.17 156.71C162.73 156.71 146.16 172.161 146.16 191.22C146.16 210.279 162.73 225.73 183.17 225.73Z"
                fill={`url(#${prefix}P10)`}
              />
              {/* Pawn spot 2 top-right — shadow */}
              <path
                d="M322.7 221.69C343.14 221.69 359.71 206.239 359.71 187.18C359.71 168.121 343.14 152.67 322.7 152.67C302.26 152.67 285.69 168.121 285.69 187.18C285.69 206.239 302.26 221.69 322.7 221.69Z"
                fill={pawnSpotShadow}
              />
              {/* Pawn spot 2 top-right — fill (player-specific) */}
              <path
                d="M322.7 224.39C343.14 224.39 359.71 208.939 359.71 189.88C359.71 170.821 343.14 155.37 322.7 155.37C302.26 155.37 285.69 170.821 285.69 189.88C285.69 208.939 302.26 224.39 322.7 224.39Z"
                fill={`url(#${prefix}P11)`}
              />
              {/* Pawn spot 3 bottom-left — shadow */}
              <path
                d="M183.15 354.98C203.59 354.98 220.16 339.529 220.16 320.47C220.16 301.411 203.59 285.96 183.15 285.96C162.71 285.96 146.14 301.411 146.14 320.47C146.14 339.529 162.71 354.98 183.15 354.98Z"
                fill={pawnSpotShadow}
              />
              {/* Pawn spot 3 bottom-left — fill (player-specific) */}
              <path
                d="M183.15 357.68C203.59 357.68 220.16 342.229 220.16 323.17C220.16 304.111 203.59 288.66 183.15 288.66C162.71 288.66 146.14 304.111 146.14 323.17C146.14 342.229 162.71 357.68 183.15 357.68Z"
                fill={`url(#${prefix}P12)`}
              />
              {/* Pawn spot 4 bottom-right — shadow */}
              <path
                d="M322.7 355.66C343.14 355.66 359.71 340.209 359.71 321.15C359.71 302.091 343.14 286.64 322.7 286.64C302.26 286.64 285.69 302.091 285.69 321.15C285.69 340.209 302.26 355.66 322.7 355.66Z"
                fill={pawnSpotShadow}
              />
              {/* Pawn spot 4 bottom-right — fill (player-specific) */}
              <path
                d="M322.7 358.35C343.14 358.35 359.71 342.899 359.71 323.84C359.71 304.78 343.14 289.33 322.7 289.33C302.26 289.33 285.69 304.78 285.69 323.84C285.69 342.899 302.26 358.35 322.7 358.35Z"
                fill={`url(#${prefix}P13)`}
              />
            </>
          )}

          {/* Shadow accent paths */}
          <path
            d="M417.43 240.41V247.38L405.34 275.38L404.57 272.67L417.43 240.41Z"
            fill={`url(#${prefix}P14)`}
          />
          <path
            d="M103.15 327.07L86.1001 316.3V313.73L110.27 325.54L103.15 327.07Z"
            fill={`url(#${prefix}P15)`}
          />
          <path
            d="M402.71 247.56L403.07 252.24L395.71 270.27L393.53 267.75L402.71 247.56Z"
            fill={`url(#${prefix}P16)`}
          />

          {/* Left torch — small accent (player-specific) */}
          <path
            d="M70.9099 199.06C69.7999 196.21 67.8599 196.12 64.4399 193.13C57.8999 187.43 63.3399 203.9 62.1299 198.53C61.9899 197.91 61.6299 196.12 60.5899 195.85C59.3699 195.54 47.0099 170.48 46.6199 171C42.7999 176.1 46.2899 189.67 52.4399 200.82C53.7099 203.12 55.4599 205.99 57.8399 209.15C60.5599 212.76 65.9699 212.9 68.8599 209.43C69.0299 209.23 69.1999 209.01 69.3599 208.78C71.2899 206.07 72.1599 202.24 70.9199 199.06H70.9099Z"
            fill={`url(#${prefix}P17)`}
          />

          {/* Top-right torch — small fixed */}
          <path
            d="M412.56 81.37C412.73 78.74 411.25 77.96 409.66 74.37C406.63 67.53 404.62 60 405.63 55.36C405.75 54.83 406.12 53.29 405.4 52.71C404.56 52.02 404.29 50.32 403.8 50.58C398.95 53.17 396.73 65.05 397.47 76C397.66 78.79 398.1 82.52 399.21 86.89C399.6 88.41 400.93 89.52 402.49 89.6C403.97 89.68 405.88 89.47 407.78 88.39C410.27 86.98 412.35 84.3 412.55 81.36L412.56 81.37Z"
            fill={`url(#${prefix}P18)`}
          />

          {/* Top-right torch — large fixed */}
          <path
            d="M410.1 94.3601C409.65 87.4101 413.55 85.3601 417.74 75.8901C419.42 72.0901 420.79 67.9001 421.89 63.5401C422.64 60.5701 429.66 51.1601 430.16 48.1001C430.46 46.2801 422.87 57.0601 423.08 55.2501C424.5 43.2501 425.61 25.4701 424.1 18.5801C423.79 17.1701 422.81 13.1201 424.71 11.5701C426.93 9.75009 431.88 12.3901 433.18 13.0801C437.52 15.4001 442.48 26.9201 445.19 33.8401C446.04 36.0101 439.32 30.9101 440 33.4001C440.53 35.3201 447.08 38.3901 447.51 40.4701C449.98 52.3701 450.8 66.5601 449.88 80.2001C449.3 88.7801 447.8 100.75 443.67 114.83C442.04 115.37 432.32 118.39 422.65 112.92C416.06 109.19 410.58 102.12 410.07 94.3701L410.1 94.3601Z"
            fill={`url(#${prefix}P19)`}
          />

          {/* Top-left torch — fixed */}
          <path
            d="M77.1699 158.63C76.0799 152.96 72.5199 152.13 67.1899 145.42C65.9699 143.88 61.3099 142.21 60.1899 140.46C59.2199 138.94 64.5099 142.1 63.6299 140.46C57.1199 128.3 50.4299 108.85 50.1199 100.8C50.0699 99.61 50.0099 96.16 48.1699 95.31C46.0099 94.31 42.5899 97.46 41.6999 98.29C36.4299 103.15 36.0899 123.03 37.3799 136.24C37.5999 138.47 44.6499 136.33 45.0199 138.64C45.5699 142.11 38.4099 141.55 39.2699 145.08C40.1299 148.61 41.1499 152.14 42.2999 155.58C43.8699 160.28 46.1299 166.2 49.4699 172.88C53.2799 180.5 63.2599 182.61 69.8099 177.16C70.1999 176.84 70.5799 176.5 70.9499 176.13C75.4499 171.77 78.3699 164.96 77.1599 158.64L77.1699 158.63Z"
            fill={`url(#${prefix}P20)`}
          />

          {/* Bottom-right torch — fixed */}
          <path
            d="M419.12 348.77C420.21 343.1 423.77 342.27 429.1 335.56C435.43 327.59 440.34 316.27 443.24 306.36C443.6 305.12 445.87 308.37 446.17 307.18C447.32 302.61 448.52 296.47 448.64 293.36C448.69 292.17 446.28 286.3 448.12 285.45C450.28 284.45 453.7 287.6 454.59 288.43C463.42 296.58 461.58 322.95 453.99 345.72C452.42 350.42 450.16 356.34 446.82 363.02C443.01 370.64 433.03 372.75 426.48 367.3C426.09 366.98 425.71 366.64 425.34 366.27C420.84 361.91 417.92 355.1 419.13 348.78L419.12 348.77Z"
            fill={`url(#${prefix}P21)`}
          />

          {/* Left torch — small fixed */}
          <path
            d="M69.3599 197.9C68.2499 195.05 66.3099 194.96 62.8899 191.97C56.3499 186.27 50.9099 175.71 49.6999 170.34C49.5599 169.72 49.1999 167.93 48.1599 167.66C46.9399 167.35 45.4599 169.32 45.0699 169.84C41.2499 174.94 44.7399 188.51 50.8899 199.66C52.1599 201.96 53.9099 204.83 56.2899 207.99C59.0099 211.6 64.4199 211.74 67.3099 208.27C67.4799 208.07 67.6499 207.85 67.8099 207.62C69.7399 204.91 70.6099 201.08 69.3699 197.9H69.3599Z"
            fill={`url(#${prefix}P22)`}
          />

          {/* Bottom-left torch — accent (player-specific) */}
          <path
            d="M72.3899 402.7C71.2799 399.85 69.3399 399.76 65.9199 396.77C59.3799 391.07 64.8199 407.54 63.6099 402.17C63.4699 401.55 63.1099 399.76 62.0699 399.49C60.8499 399.18 48.4899 374.12 48.0999 374.64C44.2799 379.74 47.7699 393.31 53.9199 404.46C55.1899 406.76 56.9399 409.63 59.3199 412.79C62.0399 416.4 67.4499 416.54 70.3399 413.07C70.5099 412.87 70.6799 412.65 70.8399 412.42C72.7699 409.71 73.6399 405.88 72.3999 402.7H72.3899Z"
            fill={`url(#${prefix}P23)`}
          />

          {/* Bottom-left torch — small fixed */}
          <path
            d="M70.9099 400.63C69.7999 397.78 67.8599 397.69 64.4399 394.7C57.8999 389 52.4599 378.44 51.2499 373.07C51.1099 372.45 50.7499 370.66 49.7099 370.39C48.4899 370.08 47.0099 372.05 46.6199 372.57C42.7999 377.67 46.2899 391.24 52.4399 402.39C53.7099 404.69 55.4599 407.56 57.8399 410.72C60.5599 414.33 65.9699 414.47 68.8599 411C69.0299 410.8 69.1999 410.58 69.3599 410.35C71.2899 407.64 72.1599 403.81 70.9199 400.63H70.9099Z"
            fill={`url(#${prefix}P24)`}
          />
        </g>
      </g>

      {/* Left badge display when player has voluntarily left the game */}
      {hasLeft && (
        <g>
          <circle cx="245" cy="246" r="100" fill="#666" />
          <circle cx="245" cy="246" r="85" fill="#444" />
          <g transform="translate(185, 186)">
            <rect
              x="15"
              y="10"
              width="50"
              height="100"
              rx="4"
              fill="#888"
              stroke="#aaa"
              strokeWidth="3"
            />
            <rect x="20" y="15" width="40" height="90" rx="2" fill="#666" />
            <circle cx="52" cy="60" r="4" fill="#ccc" />
            <path
              d="M75 60 L100 60 M90 50 L100 60 L90 70"
              stroke="#fff"
              strokeWidth="6"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </g>
        </g>
      )}

      {/* Rank badge display when player has finished (but not left) */}
      {playerRank !== undefined && !hasLeft && (
        <g>
          <circle cx="245" cy="246" r="100" fill="#130F16" />
          <circle cx="245" cy="246" r="90" fill={surfaceSecondary} />
          <text
            x="245"
            y="227"
            textAnchor="middle"
            fontSize="72"
            aria-label={`Rank ${playerRank.toString()} trophy`}
          >
            {getRankIcon(playerRank)}
          </text>
          <text x="245" y="297" textAnchor="middle" fontSize="48" fontWeight="bold" fill="#fff">
            {getRankOrdinal(playerRank)}
          </text>
        </g>
      )}

      {/* Render pawns conditionally (hidden when player has finished or left) */}
      {playerRank === undefined && !hasLeft && (
        <>
          {renderPawnAtPosition(
            'pawn1',
            pawn1,
            PawnComponent,
            {
              isActive: pawn1Active,
              isHovered: pawn1Hovered,
              isSelected: pawn1Selected,
              isMoveable: pawn1Moveable
            },
            onPawn1Click,
            shouldAnimatePawns
          )}
          {renderPawnAtPosition(
            'pawn2',
            pawn2,
            PawnComponent,
            {
              isActive: pawn2Active,
              isHovered: pawn2Hovered,
              isSelected: pawn2Selected,
              isMoveable: pawn2Moveable
            },
            onPawn2Click,
            shouldAnimatePawns
          )}
          {renderPawnAtPosition(
            'pawn3',
            pawn3,
            PawnComponent,
            {
              isActive: pawn3Active,
              isHovered: pawn3Hovered,
              isSelected: pawn3Selected,
              isMoveable: pawn3Moveable
            },
            onPawn3Click,
            shouldAnimatePawns
          )}
          {renderPawnAtPosition(
            'pawn4',
            pawn4,
            PawnComponent,
            {
              isActive: pawn4Active,
              isHovered: pawn4Hovered,
              isSelected: pawn4Selected,
              isMoveable: pawn4Moveable
            },
            onPawn4Click,
            shouldAnimatePawns
          )}
        </>
      )}
    </svg>
  );
});

export default MagmaBaseComponent;
